import { ChartType } from '@adtrackify/at-tracking-event-types';

import { DASHBOARD_DENSITY } from '@/features/dashboard/types/dashboard-density-enums';

import { BREAKPOINT_TIER } from '../types/grid-enums';
import { COLS_BY_TIER } from '../constants/grid-constants';
import type { WidgetDimensions, WidgetTypeBounds } from '../types/overview-types';
import {
  FULL_WIDTH_BOUNDS,
  HEIGHT_BOUNDS_BY_TYPE,
  STANDARD_WIDTH_BOUNDS,
} from '../constants/widget-sizes-constants';
import { WIDGET_DISPLAY_SIZE } from '../types/widget-sizes-enums';

const WIDTH_BY_TIER_AND_SIZE: Record<BREAKPOINT_TIER, Record<WIDGET_DISPLAY_SIZE, number>> = {
  [BREAKPOINT_TIER.DESKTOP]: {
    [WIDGET_DISPLAY_SIZE.COMPACT]: 3, // 4 across
    [WIDGET_DISPLAY_SIZE.NORMAL]: 4, // 3 across
    [WIDGET_DISPLAY_SIZE.LARGE]: 6, // 2 across
  },
  [BREAKPOINT_TIER.TABLET]: {
    [WIDGET_DISPLAY_SIZE.COMPACT]: 2, // 3 across
    [WIDGET_DISPLAY_SIZE.NORMAL]: 3, // 2 across
    [WIDGET_DISPLAY_SIZE.LARGE]: 6, // 1 across
  },
  [BREAKPOINT_TIER.MOBILE]: {
    // Phones use two standard widgets per row in Compact/Normal. Large and
    // table-like widgets still take the full 6-col row.
    [WIDGET_DISPLAY_SIZE.COMPACT]: 3,
    [WIDGET_DISPLAY_SIZE.NORMAL]: 3,
    [WIDGET_DISPLAY_SIZE.LARGE]: 6,
  },
};

const HEIGHT_BY_TYPE_AND_SIZE: Record<string, Record<WIDGET_DISPLAY_SIZE, number>> = {
  // Sparklines: trend-only, no axes — always 1 row regardless of preset.
  // Width changes between presets, height does not.
  [ChartType.SPARKLINE]: {
    [WIDGET_DISPLAY_SIZE.COMPACT]: 1,
    [WIDGET_DISPLAY_SIZE.NORMAL]: 1,
    [WIDGET_DISPLAY_SIZE.LARGE]: 1,
  },
  // KPI / metric cards: number + small trend. h=1 reads great for the
  // number; h=2 only earned at Large.
  [ChartType.KPI]: {
    [WIDGET_DISPLAY_SIZE.COMPACT]: 1,
    [WIDGET_DISPLAY_SIZE.NORMAL]: 1,
    [WIDGET_DISPLAY_SIZE.LARGE]: 2,
  },
  metric: {
    [WIDGET_DISPLAY_SIZE.COMPACT]: 1,
    [WIDGET_DISPLAY_SIZE.NORMAL]: 1,
    [WIDGET_DISPLAY_SIZE.LARGE]: 2,
  },
  // Full-feature charts: h=2 is the readable minimum (axes + plot area).
  // Large bumps to h=3 for a hero chart.
  [ChartType.LINE]: {
    [WIDGET_DISPLAY_SIZE.COMPACT]: 2,
    [WIDGET_DISPLAY_SIZE.NORMAL]: 2,
    [WIDGET_DISPLAY_SIZE.LARGE]: 3,
  },
  [ChartType.AREA]: {
    [WIDGET_DISPLAY_SIZE.COMPACT]: 2,
    [WIDGET_DISPLAY_SIZE.NORMAL]: 2,
    [WIDGET_DISPLAY_SIZE.LARGE]: 3,
  },
  [ChartType.BAR]: {
    [WIDGET_DISPLAY_SIZE.COMPACT]: 2,
    [WIDGET_DISPLAY_SIZE.NORMAL]: 2,
    [WIDGET_DISPLAY_SIZE.LARGE]: 3,
  },
  // Tables: short by default so they don't blow out the viewport. Large
  // bumps from 4 to 6 rows for users who want a tall data view.
  [ChartType.TABLE]: {
    [WIDGET_DISPLAY_SIZE.COMPACT]: 4,
    [WIDGET_DISPLAY_SIZE.NORMAL]: 4,
    [WIDGET_DISPLAY_SIZE.LARGE]: 6,
  },
  [ChartType.DIMENSION_TABLE]: {
    [WIDGET_DISPLAY_SIZE.COMPACT]: 4,
    [WIDGET_DISPLAY_SIZE.NORMAL]: 4,
    [WIDGET_DISPLAY_SIZE.LARGE]: 6,
  },
  [ChartType.ATTRIBUTION_TABLE]: {
    [WIDGET_DISPLAY_SIZE.COMPACT]: 3,
    [WIDGET_DISPLAY_SIZE.NORMAL]: 3,
    [WIDGET_DISPLAY_SIZE.LARGE]: 4,
  },
};

const FULL_WIDTH_CHART_TYPES = new Set<string>([
  ChartType.TABLE,
  ChartType.DIMENSION_TABLE,
  ChartType.ATTRIBUTION_TABLE,
]);

function isFullWidthType(chartType?: string): boolean {
  return chartType !== undefined && FULL_WIDTH_CHART_TYPES.has(chartType);
}

export function getWidgetTypeBounds(chartType?: string): WidgetTypeBounds {
  const heightBounds = HEIGHT_BOUNDS_BY_TYPE[chartType ?? ''] ??
    HEIGHT_BOUNDS_BY_TYPE[ChartType.AREA];
  const widthBounds = isFullWidthType(chartType) ? FULL_WIDTH_BOUNDS : STANDARD_WIDTH_BOUNDS;
  return { ...widthBounds, ...heightBounds };
}

function getHeightForSize(chartType: string | undefined, size: WIDGET_DISPLAY_SIZE): number {
  return (
    HEIGHT_BY_TYPE_AND_SIZE[chartType ?? '']?.[size] ??
    HEIGHT_BY_TYPE_AND_SIZE[ChartType.AREA][size]
  );
}

export function getDimensionsForDisplaySize(
  chartType: string | undefined,
  size: WIDGET_DISPLAY_SIZE,
  tier: BREAKPOINT_TIER = BREAKPOINT_TIER.DESKTOP
): WidgetDimensions {
  const cols = COLS_BY_TIER[tier];
  const w = isFullWidthType(chartType) ? cols : WIDTH_BY_TIER_AND_SIZE[tier][size];
  const h = getHeightForSize(chartType, size);
  return { w, h };
}

const DENSITY_TO_PRESET: Record<DASHBOARD_DENSITY, WIDGET_DISPLAY_SIZE> = {
  [DASHBOARD_DENSITY.COMPACT]: WIDGET_DISPLAY_SIZE.COMPACT,
  [DASHBOARD_DENSITY.NORMAL]: WIDGET_DISPLAY_SIZE.NORMAL,
};

export function getDimensionsForDensity(
  chartType: string | undefined,
  density: DASHBOARD_DENSITY,
  tier: BREAKPOINT_TIER
): WidgetDimensions {
  const preset = DENSITY_TO_PRESET[density];
  return getDimensionsForDisplaySize(chartType, preset, tier);
}
